import { PLAYER_ICONS, type HighScoreEntry, type PlayerIcon } from '../../shared/highScores';

export interface HighScorePodiumOptions {
  scores: ReadonlyArray<HighScoreEntry>;
  highlightDate?: number | null;
}

export interface HighScorePodium {
  element: HTMLDivElement;
  destroy(): void;
}

const RANK_META = {
  1: { color: 'yellow', medal: '🥇' },
  2: { color: 'inkSoft', medal: '🥈' },
  3: { color: 'orange', medal: '🥉' },
} as const satisfies Readonly<Record<1 | 2 | 3, { color: string; medal: string }>>;

function findIcon(id: string): PlayerIcon {
  return PLAYER_ICONS.find((p) => p.id === id) ?? PLAYER_ICONS[0];
}

function buildCard(entry: HighScoreEntry, rank: 1 | 2 | 3, highlight: boolean): HTMLDivElement {
  const card = document.createElement('div');
  card.className = 'podium-card';
  card.dataset.rank = String(rank);
  if (highlight) card.dataset.highlight = 'true';
  card.dataset.testid = `podium-card-${rank}`;

  const meta = RANK_META[rank];
  const medal = document.createElement('div');
  medal.className = 'podium-medal';
  medal.textContent = meta.medal;
  card.appendChild(medal);

  const chip = document.createElement('div');
  chip.className = 'podium-chip';
  chip.dataset.color = meta.color;
  const icon = findIcon(entry.icon);
  chip.textContent = icon.emoji;
  card.appendChild(chip);

  const name = document.createElement('div');
  name.className = 'podium-name headline';
  name.textContent = entry.name ?? '—';
  card.appendChild(name);

  const score = document.createElement('div');
  score.className = 'podium-score headline';
  score.textContent = String(entry.score);
  card.appendChild(score);

  const stats = document.createElement('div');
  stats.className = 'podium-stats';
  const hits = document.createElement('span');
  hits.textContent = `🎯 ${entry.hits}`;
  const combo = document.createElement('span');
  combo.textContent = `⚡ ×${entry.bestCombo}`;
  stats.append(hits, combo);
  card.appendChild(stats);

  return card;
}

export function createHighScorePodium(opts: HighScorePodiumOptions): HighScorePodium | null {
  const { scores, highlightDate = null } = opts;
  if (!scores.length) return null;

  const top3 = scores.slice(0, 3);
  const ordered: Array<{ entry: HighScoreEntry; rank: 1 | 2 | 3 }> = [];
  if (top3[1]) ordered.push({ entry: top3[1], rank: 2 });
  if (top3[0]) ordered.push({ entry: top3[0], rank: 1 });
  if (top3[2]) ordered.push({ entry: top3[2], rank: 3 });

  const root = document.createElement('div');
  root.className = 'podium';
  root.dataset.testid = 'high-score-podium';

  for (const { entry, rank } of ordered) {
    const highlight = highlightDate !== null && entry.date === highlightDate;
    root.appendChild(buildCard(entry, rank, highlight));
  }

  return {
    element: root,
    destroy(): void {
      root.remove();
    },
  };
}
